import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandMark from '../components/BrandMark';
import {
  Check, 
  X, 
  Shield, 
  TrendingUp,
  Clock,
  Award,
  CreditCard,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const PricingPage = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState('professional');

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      description: 'Ideal para pequenas empresas',
      monthlyPrice: 297,
      yearlyPrice: 2970, // 2 meses grátis
      features: [
        { text: 'Até 50 denúncias/mês', included: true },
        { text: '3 usuários', included: true },
        { text: '1 formulário personalizado', included: true },
        { text: 'Relatórios básicos', included: true },
        { text: 'Suporte por email', included: true },
        { text: 'Backup semanal', included: true },
        { text: 'Analytics avançado', included: false },
        { text: 'API access', included: false },
        { text: 'White-label', included: false },
        { text: 'Suporte prioritário', included: false }
      ],
      color: 'blue',
      badge: null
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'Para empresas em crescimento',
      monthlyPrice: 597,
      yearlyPrice: 5970, // 2 meses grátis
      features: [
        { text: 'Até 200 denúncias/mês', included: true },
        { text: '10 usuários', included: true },
        { text: '5 formulários personalizados', included: true },
        { text: 'Relatórios avançados', included: true },
        { text: 'Suporte prioritário 24h', included: true },
        { text: 'Backup diário', included: true },
        { text: 'Analytics com IA', included: true },
        { text: 'Integrações básicas', included: true },
        { text: 'White-label', included: false },
        { text: 'API completa', included: false }
      ],
      color: 'purple',
      badge: 'MAIS POPULAR',
      recommended: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Solução completa para grandes empresas',
      monthlyPrice: 1497,
      yearlyPrice: 14970, // 2 meses grátis
      features: [
        { text: 'Denúncias ilimitadas', included: true },
        { text: 'Usuários ilimitados', included: true },
        { text: 'Formulários ilimitados', included: true },
        { text: 'Relatórios personalizados', included: true },
        { text: 'Suporte dedicado com SLA', included: true },
        { text: 'Backup em tempo real', included: true },
        { text: 'Analytics com IA avançada', included: true },
        { text: 'Todas as integrações', included: true },
        { text: 'White-label completo', included: true },
        { text: 'API completa + Webhooks', included: true }
      ],
      color: 'gradient',
      badge: 'COMPLETO'
    }
  ];

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    // Aqui integraria com Stripe/Mercado Pago
    navigate(`/checkout?plan=${planId}&billing=${billingCycle}`);
  };

  const calculateSavings = (monthly, yearly) => {
    const yearlyMonthly = monthly * 12;
    const savings = yearlyMonthly - yearly;
    const percentage = Math.round((savings / yearlyMonthly) * 100);
    return { savings, percentage };
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <BrandMark tone="light" height={40} decorative />
              <span className="text-xl font-bold text-gray-900">REPORTFius</span>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900"
              >
                Home
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:text-gray-900"
              >
                Login
              </button>
              <button 
                onClick={() => navigate('/register')}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition"
              >
                Começar Grátis
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-semibold">14 DIAS GRÁTIS • SEM CARTÃO</span>
          </div>
          
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Escolha o plano ideal para
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> sua empresa</span>
          </h1>
          
          <p className="text-xl text-gray-600 mb-8">
            Comece gratuitamente. Escale conforme cresce. Cancele quando quiser.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg font-medium transition ${
                billingCycle === 'monthly' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-lg font-medium transition ${
                billingCycle === 'yearly' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600'
              }`}
            >
              Anual
              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                Economize 17%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan) => {
              const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
              const { savings, percentage } = calculateSavings(plan.monthlyPrice, plan.yearlyPrice);
              
              return (
                <div
                  key={plan.id}
                  className={`relative bg-white rounded-2xl shadow-lg transition-all transform hover:scale-105 ${
                    plan.recommended ? 'ring-2 ring-purple-500 shadow-xl' : ''
                  }`}
                >
                  {/* Badge */}
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className={`px-4 py-1 text-xs font-bold text-white rounded-full ${
                        plan.recommended ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-900'
                      }`}>
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  <div className="p-8">
                    {/* Plan Header */}
                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                      <p className="text-gray-600">{plan.description}</p>
                    </div>

                    {/* Price */}
                    <div className="text-center mb-8">
                      <div className="flex items-end justify-center">
                        <span className="text-2xl font-medium text-gray-600">R$</span>
                        <span className="text-5xl font-bold text-gray-900 mx-1">
                          {billingCycle === 'monthly' ? price : Math.floor(price / 10)}
                        </span>
                        <span className="text-gray-600">
                          /{billingCycle === 'monthly' ? 'mês' : 'mês'}
                        </span>
                      </div>
                      
                      {billingCycle === 'yearly' && (
                        <div className="mt-2">
                          <span className="text-sm text-gray-500 line-through">
                            R$ {plan.monthlyPrice * 12}/ano
                          </span>
                          <span className="ml-2 text-sm text-green-600 font-medium">
                            Economize R$ {savings}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* CTA Button */}
                    <button
                      onClick={() => handleSelectPlan(plan.id)}
                      className={`w-full py-3 px-6 rounded-lg font-semibold transition flex items-center justify-center space-x-2 ${
                        plan.recommended
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      <span>Começar Trial Grátis</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>

                    {/* Features */}
                    <div className="mt-8 space-y-4">
                      <p className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                        O que está incluído:
                      </p>
                      {plan.features.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          {feature.included ? (
                            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={`text-sm ${
                            feature.included ? 'text-gray-700' : 'text-gray-400'
                          }`}>
                            {feature.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Segurança Total</h4>
              <p className="text-sm text-gray-600">Criptografia end-to-end e compliance LGPD</p>
            </div>
            
            <div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Suporte 24/7</h4>
              <p className="text-sm text-gray-600">Time dedicado para ajudar sua empresa</p>
            </div>
            
            <div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">99.9% Uptime</h4>
              <p className="text-sm text-gray-600">Garantia de disponibilidade com SLA</p>
            </div>
            
            <div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Award className="w-6 h-6 text-yellow-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Garantia 30 dias</h4>
              <p className="text-sm text-gray-600">Satisfação garantida ou seu dinheiro de volta</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Perguntas Frequentes
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                Posso mudar de plano depois?
              </h3>
              <p className="text-gray-600">
                Sim! Você pode fazer upgrade ou downgrade a qualquer momento. 
                A diferença será calculada proporcionalmente.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                Como funciona o trial de 14 dias?
              </h3>
              <p className="text-gray-600">
                Você tem acesso completo a todas as funcionalidades do plano escolhido 
                por 14 dias, sem precisar cadastrar cartão de crédito.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                Quais formas de pagamento são aceitas?
              </h3>
              <p className="text-gray-600">
                Aceitamos todos os cartões de crédito principais, PIX para pagamento anual, 
                e boleto bancário para empresas.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">
                Existe contrato de fidelidade?
              </h3>
              <p className="text-gray-600">
                Não! Você pode cancelar a qualquer momento, sem multas ou taxas. 
                Acreditamos na qualidade do nosso serviço.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Pronto para transformar seu canal de denúncias?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Junte-se a mais de 500 empresas que já confiam no REPORTFius
          </p>
          <button
            onClick={() => navigate('/register')}
            className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:shadow-xl transition transform hover:scale-105"
          >
            Começar Trial de 14 Dias Grátis
          </button>
          <p className="text-sm text-blue-100 mt-4">
            <CreditCard className="w-4 h-4 inline mr-1" />
            Não precisa de cartão de crédito
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm">
            © 2025 REPORTFius. Todos os direitos reservados. | 
            <a href="/terms" className="ml-2 hover:text-white">Termos</a> | 
            <a href="/privacy" className="ml-2 hover:text-white">Privacidade</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;